import React, { useState, useEffect, useRef } from 'react';
import { StoreContext } from '../Hooks/store';
import useUserInteractionTracker from '../Hooks/useUserInteractionTracker';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceStrict } from 'date-fns';
import TodoItem from './TodoItem';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import {
  List,
  ListItem,
  ListItemText,
  Checkbox,
  TextField,
  Button,
  Snackbar,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
//import CommentIcon from '@mui/icons-material/Comment';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { 
  getTodoItems, 
  createTodoItem, 
  updateTodoItemDescription, 
  toggleTodoItemCompletion, 
  deleteTodoItem, 
  updateTodoItemTimeSpent, 
  updateTodoItemStartTime,
  addTodoTimeSpentHistory,
  getTotalTimeSpentOnTodo,
  updateTodoItemSortOrder
} from '../../api/todo';
import { v4 as uuidv4 } from 'uuid';
import { inferActionVerb } from './utils/actionVerbs';

// Alert component for Snackbar
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function Todos({ todolistid }) {
  const context = React.useContext(StoreContext);
  const {
    todolistsaved,
    todolistheader,
    thisuserid,
    modaldialog,
    tododirtycheck,
    mytodolists,
    showarchiveflag,
    todolistheaderid,
    showTodos,
    isCreatingFirstList,
  } = context;

  const [, setLoadedmodal] = modaldialog;
  const [todos, setTodos] = todolistsaved;
  const [dirtytodos, setdirtytodos] = tododirtycheck;
  const [userid] = thisuserid;
  const [todolists] = mytodolists;
  const [, setShowarchived] = showarchiveflag;
  const [, settodolistid] = todolistheaderid;
  const [, setShowTodos] = showTodos;

  const [newTodo, setNewTodo] = useState('');
  const [isAddingTodo, setIsAddingTodo] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTodoIndex, setEditingTodoIndex] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [deletingTodoId, setDeletingTodoId] = useState(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [activeTimers, setActiveTimers] = useState({});
  const [totalTimeSpent, setTotalTimeSpent] = useState({});
  const [timerTick, setTimerTick] = useState(0); // For timer updates
  const [redirectToCreateList, setRedirectToCreateList] = useState(false);

  const [todoHeader] = todolistheader;
  
  // Initialize the user interaction tracker
  const { trackInteraction } = useUserInteractionTracker();

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // Show Snackbar function
  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Function to navigate back to the TodoList view (All Lists)
  const handleBackToLists = () => {
    // Track the interaction
    trackInteraction('todos_back_to_lists', {
      componentName: 'Todos',
      timestamp: Date.now(),
      userId: userid
    });
    
    // Set states to show the archived lists view
    setShowTodos(false);
    setShowarchived(true);
  };

  // Check if user has any todolists
  useEffect(() => {
    if (userid && (!todolistid || !todolists || todolists.length === 0)) {
      console.log('No todolist ID or no todolists available, redirecting to create list view');
      setRedirectToCreateList(true);
      
      // Redirect to the "All Lists" view to create a todolist
      setShowTodos(false);
      setShowarchived(true);
      
      // Clear the todolist ID if it's set but invalid
      if (todolistid && (!todolists || !todolists.find(list => list.id === todolistid))) {
        settodolistid(null);
      }
      
      // Set isCreatingFirstList in the parent component to true
      // This will automatically open the CreateTodoList component with mode set to 'onStartCreating'
      const todoListComponent = context.isCreatingFirstList;
      if (todoListComponent && todoListComponent[1]) {
        todoListComponent[1](true); // Set isCreatingFirstList to true
      }
    } else {
      setRedirectToCreateList(false);
    }
  }, [userid, todolistid, todolists, setShowTodos, setShowarchived, settodolistid, context]);

  // Enhanced query to sync completed status and timers
  const queryClient = useQueryClient();
  const { 
    data: todoData,
    isLoading: todosLoading,
    error: todosError,
    refetch: refetchTodos,
    isRefetching: isRefetchingTodos
  } = useQuery({
    queryKey: ['todoItems', todolistid, userid],
    queryFn: async () => {
      try {
        if (!todolistid) return [];
        
        // Get fresh data from database
        const dbItems = await getTodoItems(todolistid);
        
        // Check for any locally running timers that should be stopped
        const storedTimers = localStorage.getItem(`todo_timers_${todolistid}`);
        const localTimers = storedTimers ? JSON.parse(storedTimers) : {};
        
        // Find todos that are completed in DB but have local timers
        const completedWithTimers = dbItems
          .filter(item => item.completed && localTimers[item.id])
          .map(item => item.id);
        
        // Stop local timers for completed items
        if (completedWithTimers.length > 0) {
          const updatedTimers = { ...localTimers };
          completedWithTimers.forEach(id => delete updatedTimers[id]);
          localStorage.setItem(`todo_timers_${todolistid}`, JSON.stringify(updatedTimers));
          setActiveTimers(updatedTimers);
        }
        
        // Check for pending changes
        const pendingChanges = localStorage.getItem(`pending_todo_changes_${userid}_${todolistid}`);
        if (pendingChanges) {
          const pending = JSON.parse(pendingChanges);
          
          // Merge DB items with pending changes, but don't override completed status from DB
          return dbItems.map(item => {
            const pendingItem = pending.find(p => p.id === item.id);
            return pendingItem ? { 
              ...item, 
              ...pendingItem,
              completed: item.completed // Always use DB completed status
            } : item;
          });
        }
        
        return dbItems;
      } catch (error) {
        console.error('Error fetching todos:', error);
        throw error;
      }
    },
    enabled: !!todolistid && !!userid, // Require both todolistid and userid
    onSuccess: (data) => {
      if (data && Array.isArray(data)) {
        // Update local state with fresh data
        setTodos(data);
        
        // Clear any pending changes that were successfully synced
        localStorage.removeItem(`pending_todo_changes_${userid}_${todolistid}`);
        
        // Fetch total time spent for each todo item
        const fetchTimeSpent = async () => {
          const timeSpentMap = {};
          for (const todo of data || []) {
            try {
              const totalMs = await getTotalTimeSpentOnTodo(todo.id);
              if (totalMs > 0) {
                timeSpentMap[todo.id] = totalMs;
              }
            } catch (error) {
              console.error(`Error fetching time spent for todo ${todo.id}:`, error);
            }
          }
          setTotalTimeSpent(timeSpentMap);
        };
        
        fetchTimeSpent();
      } else {
        setTodos([]);
      }
    },
    onError: (error) => {
      console.error('Error fetching todos:', error);
      showSnackbar('Error fetching todos', 'error');
    }
  });
  
  // Load active timers from database and localStorage when component mounts or todolistid changes
  useEffect(() => {
    if (!todolistid || !userid) return;
    
    let isMounted = true;
    
    const loadActiveTimers = async () => {
      try {
        // First load from localStorage for immediate display
        const storedTimers = localStorage.getItem(`todo_timers_${todolistid}`);
        let localTimers = {};
        if (storedTimers) {
          try {
            localTimers = JSON.parse(storedTimers);
            if (isMounted) {
              setActiveTimers(localTimers);
              console.log('Loaded active timers from localStorage:', localTimers);
            }
          } catch (error) {
            console.error('Error parsing stored timers:', error);
          }
        }
        
        // Then fetch from database for cross-device sync
        const { getActiveTimers } = require('../../api/todo');
        const dbTimers = await getActiveTimers(userid);
        
        if (!isMounted) return;
        
        if (dbTimers && dbTimers.length > 0) {
          // Convert array of timer objects to the format used by the component
          const timerMap = {};
          dbTimers.forEach(timer => {
            if (timer.todo_id) {
              timerMap[timer.todo_id] = timer.started_at;
            }
          });
          
          console.log('Loaded active timers from database:', timerMap);
          
          // Merge with local timers (local takes precedence if conflict)
          const mergedTimers = { ...timerMap, ...localTimers };
          
          // Only update if there are new timers from the database
          const hasNewTimers = Object.keys(timerMap).some(id => !localTimers[id]);
          if (hasNewTimers) {
            // Update state and localStorage
            setActiveTimers(mergedTimers);
            localStorage.setItem(`todo_timers_${todolistid}`, JSON.stringify(mergedTimers));
          }
        }
      } catch (error) {
        console.error('Error loading active timers from database:', error);
      }
    };
    
    loadActiveTimers();
    
    return () => {
      isMounted = false;
    };
  }, [todolistid, userid]); // Removed activeTimers from dependencies to prevent infinite loop

  // Reset state when todolistid changes and ensure proper sync
  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    
    const handleTodolistChange = async () => {
      if (!todolistid || !isMounted) return;
      
      console.log('Handling todolist change for:', todolistid);
      
      // Clear todos state when todolistid changes
      setTodos([]);
      
      // Clear total time spent state
      setTotalTimeSpent({});
      
      // Note: We don't clear activeTimers here anymore since we load them from localStorage

      try {
        // Always invalidate and refetch when todolistid changes
        await queryClient.invalidateQueries(['todoItems', todolistid, userid]);
        await refetchTodos();
      } catch (error) {
        console.error('Error refreshing todos:', error);
      }
    };

    // Add a small delay to prevent rapid successive calls
    timeoutId = setTimeout(handleTodolistChange, 100);
    
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [todolistid, userid, queryClient, refetchTodos, setTodos]); // Include all dependencies

  // Track page_view when the component mounts with todolist context
  useEffect(() => {
    if (todolistid && userid) {
      trackInteraction('todo_list_items_view', {
        componentName: 'Todos',
        timestamp: Date.now(),
        userId: userid,
        todoListId: todolistid,
        itemCount: todos?.length || 0
      });
    }
  }, [todolistid, userid]);
  
  // Set up a timer to update the display every second
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimerTick(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(timerInterval);
  }, []);

  // Format time spent in a human-readable format
  const formatTimeSpent = (timeSpent) => {
    if (isNaN(timeSpent)) return '0s';
    const seconds = Math.floor(timeSpent / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Calculate and format ongoing time with precise 1-second increments
  const calculateOngoingTime = (todoId) => {
    if (!activeTimers[todoId]) return '';
    
    // Get the start time from activeTimers
    const startTime = new Date(activeTimers[todoId]);
    
    // Calculate elapsed time since the timer started
    const now = new Date();
    const elapsedSinceStart = now - startTime;
    
    // Add the elapsed time to the accumulated time from totalTimeSpent
    const accumulatedTime = totalTimeSpent[todoId] || 0;
    const totalElapsed = accumulatedTime + elapsedSinceStart;
    
    // Calculate total seconds
    const totalSeconds = Math.floor(totalElapsed / 1000);
    const seconds = totalSeconds % 60;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const hours = Math.floor(totalSeconds / 3600);

    // Format display string
    const displayStr = `${hours > 0 ? `${hours}h ` : ''}${
      minutes > 0 || hours > 0 ? `${minutes}m ` : ''
    }${seconds}s`;

    return displayStr;
  };

  // Handle adding a new todo
  const handleAddedTodo = async () => {
    if (newTodo.trim() === '') {
      showSnackbar('Todo cannot be empty', 'error');
      return;
    }
    
    if (!todolistid) {
      showSnackbar('No todo list selected', 'error');
      return;
    }
  
    // Optimistically update the local state
    const tempTodo = {
      id: uuidv4(), // Temporary ID (will be replaced by the API response)
      user_id: userid,
      list_id: todolistid,
      description: newTodo.trim(),
      completed: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  
    // Optimistically add the temporary todo while preserving existing items
    setTodos(prevTodos => {
      const currentTodos = prevTodos && prevTodos.length > 0 ? prevTodos : (todoData || []);
      return [...currentTodos, tempTodo];
    });
  
    // Clear the input field
    setNewTodo('');
    setIsAddingTodo(false);
  
    try {
      // Call the API to create the todo item
      const newTodoItem = await createTodoItem(userid, todolistid, newTodo.trim(), false);
      
      // Update the temporary todo with the real data from API
      setTodos(prevTodos => 
        prevTodos.map(todo => 
          todo.id === tempTodo.id ? { ...newTodoItem } : todo
        )
      );
      
      // Force a refetch to ensure UI is in sync with database
      await refetchTodos();
  
      showSnackbar('Todo added!', 'success');
    } catch (error) {
      console.error('Error adding todo:', error);
      showSnackbar(`Error adding todo: ${error.message}`, 'error');
  
      // Rollback the optimistic update if the API call fails
      setTodos(prevTodos => {
        const currentTodos = prevTodos && prevTodos.length > 0 ? prevTodos : (todoData || []);
        return currentTodos.filter(todo => todo.id !== tempTodo.id);
      });
    }
  };

  // Handle toggling todo completion
  const handleToggleTodo = async (todoId) => {
    // Find the todo by ID - check both local state and query data
    const currentTodos = todos && todos.length > 0 ? todos : (todoData || []);
    const todo = currentTodos.find(t => t.id === todoId);
    
    if (!todo) {
      console.error('Todo not found with ID:', todoId);
      return;
    }

    try {
      // If the todo is being completed and has an active timer, stop the timer and mark as completed
      if (!todo.completed && activeTimers[todo.id]) {
        await handleStopTimer(todo.id, true); // Pass true to indicate we want to complete the todo
        return; // handleStopTimer will update the todo status
      }
      
      // Track the completion action if the todo is being completed (not uncompleted)
      if (!todo.completed) {
        trackInteraction('todo_complete', {
          componentName: 'Todos',
          timestamp: Date.now(),
          userId: userid
        });
      }

      // If the todo is being completed, calculate the time between start and completion
      let timeSpentText = null;
      let timeSpentMs = 0;
      let startTimeISO = null;
      
      if (!todo.completed) {
        // This is the case where a todo is being completed
        const completionTime = new Date();
        const endTimeISO = completionTime.toISOString();
        let startTime;
        
        // Use started_at if available, otherwise fall back to created_at
        if (todo.started_at) {
          startTime = new Date(todo.started_at);
          startTimeISO = todo.started_at;
          timeSpentMs = completionTime - startTime;
          timeSpentText = `Completed in: ${formatTimeSpent(timeSpentMs)}`;
        } else if (todo.created_at) {
          startTime = new Date(todo.created_at);
          startTimeISO = todo.created_at;
          timeSpentMs = completionTime - startTime;
          timeSpentText = `Completed in: ${formatTimeSpent(timeSpentMs)}`;
        }
        
        if (timeSpentText) {
          todo.time_spent_text = timeSpentText;
          todo.ended_at = endTimeISO;
          
          // Only add a record to the time spent history table if the todo doesn't already have time spent
          // This prevents double-counting when completing a todo after stopping the timer
          if (timeSpentMs > 0 && startTimeISO && !todo.time_spent) {
            try {
              await addTodoTimeSpentHistory(todo.id, userid, timeSpentMs, startTimeISO, endTimeISO);
              
              // Update the total time spent for this todo
              const totalMs = await getTotalTimeSpentOnTodo(todo.id);
              if (totalMs > 0) {
                setTotalTimeSpent(prev => ({
                  ...prev,
                  [todo.id]: totalMs
                }));
              }
            } catch (error) {
              console.error('Error adding time spent history:', error);
            }
          }
        }
      }

      // Optimistically update local state first
      setTodos(prevTodos => 
        prevTodos.map(t => 
          t.id === todo.id ? { ...t, completed: !todo.completed } : t
        )
      );

      // Update in database
      await toggleTodoItemCompletion(todo.id, !todo.completed, timeSpentText);
      
      // Force refresh and invalidate cache to ensure sync across devices
      await Promise.all([
        refetchTodos(),
        queryClient.invalidateQueries(['todoItems', todolistid]),
        queryClient.invalidateQueries(['todoLists', userid])
      ]);
      
      showSnackbar(todo.completed ? 'Todo marked as incomplete' : 'Todo completed!', 'success');
      
      // Track the completion action
      trackInteraction(todo.completed ? 'todo_uncomplete' : 'todo_complete', {
        componentName: 'Todos',
        timestamp: Date.now(),
        userId: userid,
        todoId: todo.id
      });
    } catch (error) {
      console.error('Error toggling todo:', error);
      // Rollback optimistic update
      setTodos(prevTodos => 
        prevTodos.map(t => 
          t.id === todo.id ? { ...t, completed: todo.completed } : t
        )
      );
      showSnackbar('Error toggling todo', 'error');
    }
  };

  // Handle saving a todo (updating description)
  const handleSaveTodo = async (todoId, newDescription) => {
    if (!todoId || !newDescription) return;
    
    try {
      // Optimistically update local state first
      setTodos(prevTodos => 
        prevTodos.map(todo => 
          todo.id === todoId ? { ...todo, description: newDescription } : todo
        )
      );

      await updateTodoItemDescription(todoId, newDescription);
      setEditingTodoIndex(null);
      setIsEditing(false);
      showSnackbar('Todo saved!', 'success');
      
      // Invalidate cache to ensure sync across devices
      await queryClient.invalidateQueries(['todoItems', todolistid]);
    } catch (error) {
      console.error('Error saving todo:', error);
      showSnackbar('Error saving todo', 'error');
    }
  };

  // Handle deleting a todo
  const handleDeleteTodo = (todoId) => {
    if (!todoId) {
      console.error('No todo ID provided');
      return;
    }
    
    // Find the todo by ID - check both local state and query data
    const currentTodos = todos && todos.length > 0 ? todos : (todoData || []);
    const todo = currentTodos.find(t => t.id === todoId);
    
    if (!todo) {
      console.error('Todo not found with ID:', todoId);
      return;
    }

    setDeletingTodoId(todoId);
    setIsAlertOpen(true);
  };

  // Handle confirming todo deletion
  const handleConfirmDeleteTodo = async () => {
    try {
      if (!deletingTodoId) {
        console.error('No todo ID to delete');
        return;
      }
      
      // Optimistically remove from local state first
      setTodos(prev => prev.filter(todo => todo.id !== deletingTodoId));
      
      // Delete from database
      await deleteTodoItem(deletingTodoId);
      
      // Reset state
      setIsAlertOpen(false);
      setDeletingTodoId(null);
      
      // Force refresh of both todos and lists
      await Promise.all([
        refetchTodos(),
        queryClient.invalidateQueries(['todoLists']),
      ]);
      
      showSnackbar('Todo deleted!', 'success');
      
      // Track the delete action
      trackInteraction('todo_delete', {
        componentName: 'Todos',
        timestamp: Date.now(),
        userId: userid
      });
    } catch (error) {
      console.error('Error deleting todo:', error);
      // Rollback optimistic update
      refetchTodos();
      showSnackbar('Error deleting todo', 'error');
    }
  };

  // Handle canceling todo deletion
  const handleCancelDeleteTodo = () => {
    setIsAlertOpen(false);
    setDeletingTodoId(null);
  };

  // State for confirmation dialog when starting timer on completed todo
  const [confirmStartDialogOpen, setConfirmStartDialogOpen] = useState(false);
  const [todoToStart, setTodoToStart] = useState(null);

  // Start timer for a todo item
  const handleStartTimer = async (todoId) => {
    // Get the current todos array - prioritize local state over query data
    const currentTodos = todos && todos.length > 0 ? todos : (todoData || []);
    
    // Find the todo item - ensure we're searching in the correct array
    const todo = currentTodos.find(todo => todo.id === todoId);
    
    if (!todo) {
      console.error('Todo not found with ID:', todoId);
      return;
    }
    
    // If the todo is completed, show confirmation dialog
    if (todo.completed) {
      setTodoToStart(todo);
      setConfirmStartDialogOpen(true);
      return;
    }
    
    // Otherwise, start the timer normally
    await startTimerForTodo(todoId);
  };
  
  // Function to actually start the timer after confirmation if needed
  const startTimerForTodo = async (todoId) => {
    const { startTimer } = require('../../api/todo');
    try {
      // Start the timer and update the todo item's start time
      const { started_at } = await startTimer(todoId, userid);
      
      // Track the start action
      trackInteraction('todo_start', {
        componentName: 'Todos',
        timestamp: Date.now(),
        userId: userid
      });
      
      // Get current todos
      const currentTodos = todos.length > 0 ? todos : todoData || [];
      const updatedTodos = [...currentTodos];
      const todoIndex = updatedTodos.findIndex(todo => todo.id === todoId);
      
      if (todoIndex === -1) {
        console.error('Todo not found in list with ID:', todoId);
        return;
      }
      
      // If todo was completed, mark as not completed and set new start time
      if (updatedTodos[todoIndex].completed) {
        updatedTodos[todoIndex].completed = false;
        updatedTodos[todoIndex].started_at = started_at; // Use the new start time
        updatedTodos[todoIndex].ended_at = null;
        updatedTodos[todoIndex].time_spent_text = null;
        await toggleTodoItemCompletion(todoId, false);
      }
      
      // Update local state
      const updatedTimers = {
        ...activeTimers,
        [todoId]: started_at
      };
      
      setActiveTimers(updatedTimers);
      localStorage.setItem(`todo_timers_${todolistid}`, JSON.stringify(updatedTimers));
      
      updatedTodos[todoIndex].started_at = started_at;
      setTodos(updatedTodos);
      
      showSnackbar('Timer started!', 'info');
    } catch (error) {
      console.error('Error starting timer:', error);
      showSnackbar('Timer started, but failed to save start time', 'warning');
    }
  };
  
  // Handle confirming start timer on completed todo
  const handleConfirmStartTimer = async () => {
    if (!todoToStart) return;
    
    await startTimerForTodo(todoToStart.id);
    setConfirmStartDialogOpen(false);
    setTodoToStart(null);
  };
  
  // Handle canceling start timer on completed todo
  const handleCancelStartTimer = () => {
    setConfirmStartDialogOpen(false);
    setTodoToStart(null);
  };
  
  // Handle drag and drop to reorder todo items
  const handleDragEnd = async (result) => {
    // If dropped outside the list or no destination
    if (!result.destination) return;
    
    // If dropped in the same position
    if (result.destination.index === result.source.index) return;
    
    try {
      // Get the current todos array
      const currentTodos = todos.length > 0 ? todos : todoData || [];
      const updatedTodos = [...currentTodos];
      
      // Get the dragged item
      const [draggedItem] = updatedTodos.splice(result.source.index, 1);
      
      // Insert the dragged item at the new position
      updatedTodos.splice(result.destination.index, 0, draggedItem);
      
      // Update the local state optimistically
      setTodos(updatedTodos);
      
      // Track the reorder action
      trackInteraction('todo_reorder', {
        componentName: 'Todos',
        timestamp: Date.now(),
        userId: userid,
        todoId: draggedItem.id,
        fromIndex: result.source.index,
        toIndex: result.destination.index
      });
      
      // Update the sort order in the database
      await updateTodoItemSortOrder(
        draggedItem.id, 
        result.destination.index,
        todolistid
      );
      
      // Force a refetch to ensure UI is in sync with database
      await refetchTodos();
      
      showSnackbar('Todo order updated!', 'success');
    } catch (error) {
      console.error('Error updating todo order:', error);
      showSnackbar('Error updating todo order', 'error');
      
      // Refetch to restore the original order
      await refetchTodos();
    }
  };
  
  // Handle duplicating a todo item
  const handleDuplicateTodo = async (todo) => {
    if (!todo || !todolistid || !userid) {
      console.error('Missing required data for todo copying');
      return;
    }
    
    try {
      // Call the API to create a duplicate todo item
      const newTodoItem = await createTodoItem(userid, todolistid, todo.description, false);
      
      // Track the duplication action
      trackInteraction('todo_duplicate', {
        componentName: 'Todos',
        timestamp: Date.now(),
        userId: userid,
        todoId: todo.id
      });
      
      // Add the new todo to the list
      setTodos(prevTodos => {
        const currentTodos = prevTodos && prevTodos.length > 0 ? prevTodos : (todoData || []);
        return [...currentTodos, newTodoItem];
      });
      
      // Force a refetch to ensure UI is in sync with database
      await refetchTodos();
      
      showSnackbar('Todo copied!', 'success');
    } catch (error) {
      console.error('Error copying todo:', error);
      showSnackbar(`Error copying todo: ${error.message}`, 'error');
    }
  };

  // Stop timer for a todo item
  const handleStopTimer = async (todoId, completeAfterStop = false) => {
    if (!activeTimers[todoId]) {
      console.error('No active timer found for todo ID:', todoId);
      return;
    }

    // Get the current todos array
    const currentTodos = todos.length > 0 ? todos : todoData || [];
    const updatedTodos = [...currentTodos];
    const todoIndex = updatedTodos.findIndex(todo => todo.id === todoId);
    
    if (todoIndex === -1) {
      console.error('Todo not found in list with ID:', todoId);
      return;
    }

    const todo = updatedTodos[todoIndex];
    
    // Track the stop action
    trackInteraction('todo_stop', {
      componentName: 'Todos',
      timestamp: Date.now(),
      userId: userid
    });

    try {
      // Stop the timer and get time spent
      const { stopTimer } = require('../../api/todo');
      const { started_at, time_spent_ms } = await stopTimer(todoId, userid);
      
      // Calculate total time spent including historical time
      const initialTotalMs = await getTotalTimeSpentOnTodo(todoId);
      const validTotalMs = isNaN(initialTotalMs) ? 0 : initialTotalMs;
      
      const timeSpentText = completeAfterStop 
        ? `Completed in: ${formatTimeSpent(validTotalMs)}`
        : `Time spent: ${formatTimeSpent(validTotalMs)}`;
      
      // Update the todo with time tracking information
      todo.time_start = started_at;
      todo.time_end = new Date().toISOString();
      todo.time_spent_text = timeSpentText;
      
      // Update the time_spent_text in the database
      await updateTodoItemTimeSpent(todoId, timeSpentText);
      
      // If completing the todo after stopping
      if (completeAfterStop) {
        todo.completed = true;
        await toggleTodoItemCompletion(todoId, true, timeSpentText);
        showSnackbar('Timer stopped and todo completed!', 'success');
      } else {
        showSnackbar('Timer stopped and time recorded!', 'success');
      }

      // Update local state
      setTodos(updatedTodos);
      
      // Remove from active timers
      const newActiveTimers = { ...activeTimers };
      delete newActiveTimers[todoId];
      setActiveTimers(newActiveTimers);
      localStorage.setItem(`todo_timers_${todolistid}`, JSON.stringify(newActiveTimers));
      
      // Update total time spent with the latest value
      const updatedTotalMs = await getTotalTimeSpentOnTodo(todoId);
      if (updatedTotalMs > 0) {
        setTotalTimeSpent(prev => ({
          ...prev,
          [todoId]: updatedTotalMs
        }));
      }
    } catch (error) {
      console.error('Error saving time spent:', error);
      
      // Remove from active timers even if DB operation failed
      const newActiveTimers = { ...activeTimers };
      delete newActiveTimers[todoId];
      setActiveTimers(newActiveTimers);
      localStorage.setItem(`todo_timers_${todolistid}`, JSON.stringify(newActiveTimers));
      
      showSnackbar(
        error.code === '42702' 
          ? 'Timer stopped (database error fixed, please refresh)' 
          : 'Timer stopped, but failed to save time data', 
        'warning'
      );
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Redirect message if no todolist exists */}
      {redirectToCreateList && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="primary" paragraph>
            First, create a task list (like a bucket for your todos). Then, add your tasks to the list.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please use the menu button in the top left to go to "All Lists" view and create your first list.
          </Typography>
        </Box>
      )}

      {/* Only show the todo UI if not redirecting */}
      {!redirectToCreateList && (
        <div style={{ width: '100%' }}>
          {/* Back button and header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton 
              onClick={handleBackToLists} 
              sx={{ mr: 1 }} 
              title="Back to Todo Lists"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              {todolists.find(list => list.id === todolistid)?.description && (
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                  sx={{ mt: 0.5 }}
                >
                  {todolists.find(list => list.id === todolistid).description}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
              {snackbarMessage}
            </Alert>
          </Snackbar>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={isAlertOpen}
            onClose={handleCancelDeleteTodo}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
          >
            <DialogTitle id="delete-dialog-title">Delete Todo Item</DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                Are you sure you want to delete this todo item? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelDeleteTodo} color="primary">
                Cancel
              </Button>
              <Button onClick={handleConfirmDeleteTodo} color="error">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Start Timer Confirmation Dialog */}
          <Dialog
            open={confirmStartDialogOpen}
            onClose={handleCancelStartTimer}
            aria-labelledby="start-timer-dialog-title"
            aria-describedby="start-timer-dialog-description"
          >
            <DialogTitle id="start-timer-dialog-title">Continue Working on Completed Item</DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                This item is marked as completed. Do you want to continue working on it? 
                This will mark the item as not completed and start the timer.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelStartTimer} color="primary">
                Cancel
              </Button>
              <Button onClick={handleConfirmStartTimer} color="primary">
                Continue Working
              </Button>
            </DialogActions>
          </Dialog>

          {/* Main Todo List UI */}
          <div style={{ width: '100%', visibility: isAlertOpen ? 'hidden' : 'visible' }}>
            {/* Loading and Error States */}
            {todosLoading || isRefetchingTodos ? (
              <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
              </Box>
            ) : todosError ? (
              <Typography color="error" mt={4}>
                Error loading todos: {todosError.message}
              </Typography>
            ) : (
              <React.Fragment>
                {/* Add New Todo Item and Summary */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {isAddingTodo ? (
                    <React.Fragment>
                      <TextField
                        autoFocus
                        multiline
                        style={{ width: '100%' }}
                        value={newTodo}
                        onChange={(e) => setNewTodo(e.target.value)}
                        onBlur={() => {
                          if (newTodo.trim() === '') {
                            setIsAddingTodo(false);
                          }
                          if (newTodo.trim() !== '') {
                            handleAddedTodo();
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAddedTodo();
                          }
                        }}
                      />
                      <div>
                        <SaveOutlinedIcon onClick={handleAddedTodo} />
                      </div>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Button variant="contained" color="primary" onClick={() => setIsAddingTodo(true)}>
                        <AddOutlinedIcon />
                      </Button>
                      <span style={{ 
                        fontStyle: 'italic', 
                        fontSize: '0.8rem', 
                        color: '#666',
                        marginLeft: '8px'
                      }}>
                        {(todos && todos.length > 0 ? todos : (todoData || [])).filter(t => activeTimers[t.id]).length || 0} in-progress, {' '}
                        {(todos && todos.length > 0 ? todos : (todoData || [])).filter(t => t.completed).length || 0} completed{' '}
                        ({(todos && todos.length > 0 ? todos : (todoData || [])).length || 0} total)
                      </span>
                    </React.Fragment>
                  )}
                </div>

                {/* Todo List Items */}
                <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId={todolistid || 'todo-list'}>
                      {(provided) => (
                        <List
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {(todos && todos.length > 0 ? todos : (todoData || [])).length > 0 ? (
                            [...(todos && todos.length > 0 ? todos : todoData)]
                              .map((todo, index) => (
                                <TodoItem
                                  key={todo.id}
                                  todo={todo}
                                  index={index}
                                  handleToggleTodo={() => handleToggleTodo(todo.id)}
                                  handleEditTodo={() => {
                                    setEditingTodoIndex(todo.id);
                                    setEditedDescription(todo.description);
                                    setIsEditing(true);
                                  }}
                                  handleSaveTodo={handleSaveTodo}
                                  handleDeleteTodo={() => handleDeleteTodo(todo.id)}
                                  handleDuplicateTodo={handleDuplicateTodo}
                                  handleStartTimer={() => handleStartTimer(todo.id)}
                                  handleStopTimer={handleStopTimer}
                                  editingTodoIndex={editingTodoIndex}
                                  setTodos={setTodos}
                                  todos={todos}
                                  isEditing={isEditing}
                                  setIsEditing={setIsEditing}
                                  editedDescription={editedDescription}
                                  setEditedDescription={setEditedDescription}
                                  activeTimers={activeTimers}
                                  totalTimeSpent={totalTimeSpent}
                                  calculateOngoingTime={calculateOngoingTime}
                                />
                              ))
                          ) : (
                            <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                              {todolistid ? "No items in this list. Add one above!" : "Select a todo list to get started."}
                            </Typography>
                          )}
                          {provided.placeholder}
                        </List>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
